import * as apis from '@/apis/order';
import * as apisCatalogue from '@/apis/catalogue';
import * as actions from '@/actions';
import { call, put, takeLatest, select, putResolve, takeEvery } from 'redux-saga/effects';
import navigate, { getPathURL } from '@/helpers/navigation';
import { groupTopBottomErrors, groupAllErrors, shouldShoppingCartCall, getNewCartOrder, getOrderSerFlow, getHybridOrderSerFlow, getBulkOrderCart, getBulkOrderReport } from '@/helpers/order';
import { clone, isArray, isEmpty } from 'lodash';
import { getServiceNumberArray } from '@/helpers/catalogue';
import * as authApi from '@/apis/auth';

import {
  GET_ELIGIBLE_LINE_COUNT_REQUEST,
  ORDER_PAYMENT_DETAILS_REQUEST,
  SUBMIT_ORDER_REQUEST,
  ORDER_GET_ORDER_DETAILS_REQUEST,
  GET_DIST_CARD_PROFILE_REQUEST,
  PDPA_GET_ORDER_DETAILS_REQUEST,
  TERMINATE_PARENT_SVC_NUM_REQUEST,
  GET_SIM_CHARGE_REQUEST,
  WAIVER_REQUEST_STATUS_UPDATE_REQUEST,
  GET_WAIVERED_ORDER_REQUEST,
  ORDER_SAVE_SMART_TEMPLATE_REQUEST,
  EDIT_SMART_TEMPLATE_REQUEST,
  REFIT_LINE_SUBMIT_REQUEST,
  PDPA_QR_IMAGE_RET_REQUEST,
  GET_BULK_ORDER_DETAILS_BY_ITEM_ID_REQUEST
} from '../../actions/order/actionTypes';
import {
  GetEligibleLineCount,
  GetSIMCharge,
  ICreateNewOrder,
  IGetBulkOrderDetails,
  IGetOrderDetails,
  IGetPaymentDetails,
  IGetPDPAOrderDetails,
  IGetTemplateOrderDetails,
  ISaveToCart,
  ISubmitOrder
} from '@/actions/order/models/types';
import { BUY_ADD_ONS, CEASE_ADDONS, CHANGE_PLAN, MOBILE_SHARE, NEWLINE, REPLACE_SIM, REPORT_LOST, RRP_ACCESSORY, RRP_EQUIPMENT, TERMINATE_NUMBER, smartTemplateCategories } from '@/config/common';
import { setxAuthToken, setApigeeToken, setXClientHeader } from '@/helpers/axios';
import { checkPdpaEnabled, getMatchSIMonly } from '@/helpers/common';
import { ORDER_GET_TEMPLATE_ORDER_DETAILS_REQUEST } from '@/actions';
import { getNetworkType } from '@/helpers/addons/addons';

export const getOrder = (state) => state.order.currentOrder;
export const getAuth = (state) => state.auth;
export const getSerRoadshow = (state) => state.serRoadshow;
export const getCatalogue = (state) => state.catalogue;

//any issue need to be fixed , which is not related to login
function* createNewOrder({ order }: ICreateNewOrder) {
  const auth = yield select(getAuth);
  const { userInfo, currentAccount } = auth;
  if (auth && order) {
    const { email = '', phoneNo = '' } = userInfo || {};
    const { brn = '', msaheaderId = '' } = currentAccount || {};
    yield put(
      actions.createNewOrder.store({
        ...order,
        userName: email,
        mobileNumber: phoneNo,
        email,
        brn: brn,
        msaHeaderId: msaheaderId
      })
    );
  }
}

function* storeSelection({ saveToCart, toOrderDetails, recalculateOnly, checkout, callback }: ISaveToCart) {
  const order = yield select(getOrder);
  if (saveToCart && order) {
    if (order.isSmartTemplate) {
      yield put(actions.saveSmartTemplate.request(order, {}, false, true, callback));
    } else {
      yield put(actions.saveToCart.request(order, toOrderDetails, recalculateOnly, checkout, callback));
    }
  }
}

function* saveToCartSaga({ orderDetails, toOrderDetails, recalculateOnly, checkout, callback }: ISaveToCart) {
  try {
    const auth = yield select(getAuth);
    const serRoadshow = yield select(getSerRoadshow);
    const catalogue = yield select(getCatalogue);
    const { msaHeaderId, brn } = auth.currentAccount;
    const { cartDetails, shoppingCartType = '' } = orderDetails
    const pricePlanGroupId = cartDetails?.pricePlan?.pricePlanGroupId
    const simOnlyValue = pricePlanGroupId && getMatchSIMonly(pricePlanGroupId)
    const pricePlanId = cartDetails?.pricePlan?.pricePlanId;
    const serviceNumberArray = getServiceNumberArray(cartDetails?.serviceNumbers);
    const callFetchAllProduct = [CHANGE_PLAN, REPLACE_SIM, TERMINATE_NUMBER, BUY_ADD_ONS, CEASE_ADDONS, REPORT_LOST].includes(shoppingCartType)
    if (cartDetails?.isEarlyRecon) {
      orderDetails = { ...orderDetails, shoppingCartType: 'EARLY_RECONTRACT' }
    }
    let fetchProductStatus = false;
    if (callFetchAllProduct) {
      const resultStatus = yield call(apis.getFetchAllProducts, {
        msaHeaderId,
        pricePlanId,
        brn,
        serviceNumber: serviceNumberArray
      });
      fetchProductStatus = resultStatus.status === 200;
      if (!fetchProductStatus) {
        const error = { response: { data: { code: 'ERROR_FETCH_ALL_PRODUCT', message: "Something went wrong while fetching all products details" } } }
        yield put(actions.saveToCart.failure(error));
      }
    }

    const isSerFlow = getOrderSerFlow(orderDetails, serRoadshow);
    const isHybridSerFlow = getHybridOrderSerFlow(orderDetails, isSerFlow);
    const numberPoolBillAccNo = catalogue.billAccount;
    const newOrderDetails = getNewCartOrder(orderDetails, pricePlanGroupId, numberPoolBillAccNo, serRoadshow)

    if (shouldShoppingCartCall(callFetchAllProduct, fetchProductStatus)) {
      const result = yield call(apis.requestSaveToCart, newOrderDetails, recalculateOnly, checkout, simOnlyValue, isSerFlow, isHybridSerFlow);
      const errorGroup = groupTopBottomErrors(result.data);
      yield put(actions.saveToCart.success({ ...result.data, errorGroup, checkout }));
      if (callback && isEmpty(errorGroup)) {
        yield call(callback);
      }
      if (toOrderDetails) {
        yield call(navigate, 'ORDER_REVIEW', {
          cartId: result.data.shoppingCartId
        });
      }
    }
  } catch (error) {
    yield put(actions.saveToCart.failure(error));
    if (callback) {
      yield call(callback, error);
    }
  }
}

function* fetchOrderDetails({ cartId }: IGetOrderDetails) {
  try {
    const result = yield call(apis.requestCartDetails, cartId);
    const errorGroup = groupAllErrors(result.data);
    if (!isEmpty(errorGroup)) yield put(actions.updateErrorGroup(errorGroup));
    const isBulk = result.data.cartDetails?.pricePlan?.noOfLines > 1;
    const orderData = {
      ...result.data,
      cartDetails: { ...result.data.cartDetails, isBulk }
    };
    yield put(actions.getOrderDetails.success(orderData));
  } catch (error) {
    yield put(actions.getOrderDetails.failure(error));
  }
}

function* submitOrder({ orderInfo }: ISubmitOrder) {
  try {
    const {
      currentAccount: { msaheaderId }, userInfo: { email }
    } = yield select(getAuth);
    const payload = { ...orderInfo, msaHeaderId: msaheaderId, createdBy: email };
    const result = yield call(apis.submitOrder, payload);
    yield put(actions.submitOrder.success(result.data));
    if (result.data?.redirectUrl) {
      // eslint-disable-next-line no-unused-expressions
      window.location.replace(result.data.redirectUrl);
    } else {
      yield call(navigate, 'ORDER_SUCCESS', {
        orderId: result.data?.orderId
      });
    }
  } catch (error) {
    yield put(actions.updateError(error));
    yield put(actions.submitOrder.failure(error));
  }
}

function* getPaymentDetails({ merchantRefNum }: IGetPaymentDetails) {
  try {
    const pdpaToken = JSON.parse(localStorage.getItem('pdpaToken') || '{}');
    const auth = yield select(getAuth)
    const checkPdpa = checkPdpaEnabled(auth)
    if (checkPdpa) {
      const channeltoken = yield call(authApi.pdpaValidate, auth.key)
      yield call(setxAuthToken, pdpaToken.accessToken);
      yield call(setApigeeToken, channeltoken.data.access_token);
      yield call(setXClientHeader, 'EMS-ESHOP-PDPA');
    }
    const result = yield call(apis.requestPaymentDetails, merchantRefNum);
    yield put(actions.getPaymentDetails.success(result.data));
  } catch (error) {
    yield put(actions.getPaymentDetails.failure(error));
  }
}

function* getEligibleLineCount({ userInfo }: GetEligibleLineCount) {
  try {
    const result = yield call(apis.getEligibleLineCount, userInfo);
    yield put(actions.getEligibleLineCount.success(result.data));
  } catch (error) {
    yield put(actions.updateError(error));
    yield put(actions.getEligibleLineCount.failure(error));
  }
}

function* getCardProfile(reqData) {
  try {
    const payload = reqData.reqData;
    if (payload.transactionType === MOBILE_SHARE) {
      payload.transactionType = NEWLINE;
    }
    const result = yield call(apis.requestCardProfile, payload);
    yield put(actions.getDistCardProfile.success(result.data));
  } catch (error) {
    yield put(actions.getDistCardProfile.failure(error));
  }
}
function* getSimCharge({ reqData, msaHeaderId }: GetSIMCharge) {
  try {
    const payload = { cardProfile: reqData };
    const result = yield call(apis.getSimCharge, payload, msaHeaderId);
    yield put(actions.getSimCharge.success(result.data));
  } catch (error) {
    yield put(actions.getSimCharge.failure(error));
  }
}

function* fetchPDPAOrderDetails({ orderId }: IGetPDPAOrderDetails) {
  try {
    const result = yield call(apis.getPdpaOrder, orderId);
    yield put(actions.getPdpaOrder.success(result.data));
  } catch (error) {
    yield put(actions.getPdpaOrder.failure(error));
  }
}

function* terminateParentSvcNo(payload) {
  try {
    const reqPayload = payload.reqData;
    const result = yield call(apis.checkParentSvcNumber, reqPayload);
    payload.callback(result.data.svcChildlineInfoResponseList);
  } catch (error) {
    yield put(actions.terminateParentSvcNo.failure(error));
  }
}

function* updateWaiverRequestStatus(reqData) {
  try {
    const result = yield call(apis.updateWaiverRequestStatus, reqData.reqData);
    yield put(actions.updateWaiverRequestStatus.success(result.data));
    navigate('WAIVER_APPROVAL_SUCCESS')
  } catch (error) {
    yield put(actions.updateWaiverRequestStatus.failure(error));
  }
}

function* getWaiveredOrder(reqData) {
  try {
    const result = yield call(apis.getWaiveredOrder, reqData.reqData);
    yield put(actions.getWaiveredOrder.success(result.data));
  } catch (error) {
    yield put(actions.getWaiveredOrder.failure(error));
  }
}

const getServiceNumberPayload = (cartDetail, resultPlan) => {
  const getGroupID = (currentPricePlanId) => {
    const groupData = resultPlan.filter((item) => {
      return item.pricePlans.filter((item2) => item2.planId === currentPricePlanId).length > 0
    })
    return groupData.length > 0 ? groupData[0].groupId : ''
  }
  const currentPricePlanId = cartDetail?.pricePlan?.pricePlanId;
  const currentPricePlanGroupId = getGroupID(currentPricePlanId);
  const networkType = getNetworkType(currentPricePlanGroupId, currentPricePlanId, resultPlan);
  const noOfLines = cartDetail?.pricePlan?.noOfLines || 1;
  const payload = {
    numOfSvc: noOfLines,
    networkType: networkType.planCategory,
    cardProfile: cartDetail?.simCard?.simCardType || '',
    currentPage: 1,
    pageSize: noOfLines,
    simType: networkType.planTypeDescription
  }
  return payload
}

const getServiceNumberList = (serviceNumbers, noOfLines = 1) => {
  if (serviceNumbers.length < noOfLines) return [];
  const svcNos: any = [];
  serviceNumbers.forEach((num, index) => {
    if (index < noOfLines) {
      svcNos.push({
        email: "",
        name: "",
        number: num.svcNo,
        status: num.status,
      })
    }
  })
  return svcNos;
}

function* getTemplateOrderDetails({ templateId, orderType, callback }: IGetTemplateOrderDetails) {
  try {
    const result = yield call(apis.requestTemplateDetails, templateId);
    const templateDetails = yield call(apisCatalogue.getSmartTemplateById, templateId);
    if (result.data?.cartDetails) {
      const cartDetail = result.data.cartDetails;
      let updatedCartDetail = clone(result.data.cartDetails);
      yield putResolve(actions.createNewOrder.request({
        shoppingCartType: orderType,
        cartDetails: cartDetail
      }));
      if ([NEWLINE].includes(orderType)) {
        const auth = yield select(getAuth);
        const { currentAccount: { msaheaderId, brn } } = auth;
        const resultPlan = yield call(apisCatalogue.getPricePlanGroup, {
          msaHeaderId: msaheaderId,
          brn,
          transactionType: orderType
        });
        const payload = getServiceNumberPayload(cartDetail, resultPlan.data)
        const resultServiceNumber = yield call(apisCatalogue.getServiceNumber, {
          ...payload,
          reservedBy: auth.userInfo.email
        });
        const serviceNumbers = resultServiceNumber.data.svcNoList
        const serviceNumberList = getServiceNumberList(serviceNumbers, cartDetail?.pricePlan?.noOfLines)
        if (serviceNumberList.length > 0) {
          updatedCartDetail['serviceNumbers'] = serviceNumberList;
          yield putResolve(actions.storeSelection({
            selection: updatedCartDetail,
            activeStep: 1,
            saveToCart: [NEWLINE, RRP_ACCESSORY, RRP_EQUIPMENT].includes(orderType),
            toOrderDetails: [NEWLINE, RRP_ACCESSORY, RRP_EQUIPMENT].includes(orderType)
          }));
          yield putResolve(actions.getTemplateOrderDetails.success(templateDetails.data));
        } else {
          const error = { response: { data: { message: 'No SIM Card Avaible for the specified Template' } } }
          yield putResolve(actions.getTemplateOrderDetails.failure(error));
        }
      } else {
        yield putResolve(actions.storeSelection({
          selection: updatedCartDetail,
          activeStep: 1,
          saveToCart: [NEWLINE, RRP_ACCESSORY, RRP_EQUIPMENT].includes(orderType),
          toOrderDetails: [NEWLINE, RRP_ACCESSORY, RRP_EQUIPMENT].includes(orderType)
        }));
        yield putResolve(actions.getTemplateOrderDetails.success(templateDetails.data));
      }
      if (![NEWLINE, RRP_ACCESSORY, RRP_EQUIPMENT].includes(orderType)) {
        callback(templateDetails.data);
      }
    }
  } catch (error) {
    yield put(actions.getTemplateOrderDetails.failure(error));
  }
}

const getProductDetails = (cartDetails) => {
  let productDetails: any = []
  const cartDetailsKeys = Object.keys(cartDetails)
  if (cartDetailsKeys.length > 0) {
    cartDetailsKeys.forEach((item) => {
      if (['accessories', 'addons', 'bundledAddons', 'equipments', 'pricePlan', 'serviceNumbers', 'simCard'].includes(item) && Object.keys(cartDetails[item]?.length > 0)) {
        const mappingItem = {
          'equipments': 'EQUIPMENT',
          'accessories': 'ACCESSORIES',
          'addons': 'ADDON', 'bundledAddons': 'BUNDLED_ADDON',
          'pricePlan': 'PLAN'
        }
        if (item === 'equipments' || item === 'accessories') {
          cartDetails[item].forEach((device) => {
            let mappedProduct = {}
            mappedProduct['category'] = mappingItem[item]
            mappedProduct['productCode'] = device.deviceCode
            mappedProduct['productId'] = device.modelCode
            mappedProduct['productName'] = device.modelDescription
            mappedProduct['productBrand'] = device.brandDescription
            mappedProduct['productDesc'] = device.deviceDescription
            mappedProduct['quantity'] = device.quantity
            productDetails.push(mappedProduct)
          })
        } else if (item === 'addons') {
          cartDetails[item].forEach((addons) => {
            let mappedProduct = {}
            mappedProduct['category'] = mappingItem[item]
            mappedProduct['productCode'] = addons.addOnPlanId
            mappedProduct['productId'] = addons.addOnId
            mappedProduct['productGroupId'] = addons.addOnGroupId
            mappedProduct['productType'] = addons.addOnType
            productDetails.push(mappedProduct)
          })
        } else if (item === 'pricePlan' && cartDetails[item] !== null) {
          let mappedProduct = {}
          const plan = cartDetails.pricePlan
          mappedProduct['category'] = mappingItem[item]
          mappedProduct['productId'] = plan.pricePlanId
          mappedProduct['productName'] = plan.pricePlanName
          mappedProduct['productGroupId'] = plan.pricePlanGroupId
          mappedProduct['quantity'] = plan.noOfLines
          mappedProduct['contractTerm'] = plan.contractTerms
          productDetails.push(mappedProduct)
        } else if (item === 'bundledAddons') {
          cartDetails[item].forEach((bundleaddons) => {
            let mappedProduct = {}
            mappedProduct['category'] = mappingItem[item]
            mappedProduct['productName'] = bundleaddons.title
            mappedProduct['productCode'] = bundleaddons.addOnPlanId
            productDetails.push(mappedProduct)
          })
        }
      }
    })
  }
  return productDetails
}

const getProductDetails2 = (productDetails, checkoutDetails) => {
  let updateProductDetails = productDetails;
  Object.keys(checkoutDetails).forEach((item) => {
    let mappedProduct = {}
    mappedProduct['category'] = 'CHECKOUT'
    mappedProduct['productConstraint'] = isArray(checkoutDetails[item]) ? checkoutDetails[item].toString() : checkoutDetails[item]
    mappedProduct['productAction'] = item
    updateProductDetails.push(mappedProduct)
  })
  return updateProductDetails
}

function* saveSmartTemplate({ orderDetails, checkoutDetails, checkout, recalculateOnly, callback }: ISaveToCart) {
  try {
    const { cartDetails, shoppingCartType = '', deleteTemplateId = '' } = orderDetails
    const productDetails = getProductDetails(cartDetails)
    const updateProductDetails = getProductDetails2(productDetails, checkoutDetails)
    const templateData = {
      name: orderDetails?.templateName,
      category: shoppingCartType,
      username: orderDetails.userName,
      email: orderDetails.email,
      msaHeaderId: orderDetails.msaHeaderId,
      brn: orderDetails?.brn,
      simType: cartDetails?.simCard?.simCardType,
      quantity: cartDetails?.pricePlan?.noOfLines,
      products: checkout ? updateProductDetails : productDetails
    }
    if (checkout) {
      const result = yield call(apis.saveSmartTemplate, templateData);
      if (result.data.success) {
        if (deleteTemplateId) {
          yield call(apisCatalogue.deleteSmartTemplate, deleteTemplateId);
          yield put(actions.setDeleteSmartTemplateId(''))
        }
        yield put(actions.saveSmartTemplate.success({ ...templateData, templateId: result.data.templateId }));
      }
      // yield put(actions.setDeleteSmartTemplateId(templateId))
      callback(result.data.templateId)
    } else {
      if (recalculateOnly) {
        const templateOrder = {
          // templateId: orderDetails?.templateId,
          name: orderDetails?.templateName,
          msaHeaderId: orderDetails.msaHeaderId,
          recalculateOnly: true,
          shoppingCartType: shoppingCartType,
          cartDetails: cartDetails
        }
        const pricePlanGroupId = cartDetails?.pricePlan?.pricePlanGroupId
        const simOnlyValue = pricePlanGroupId && getMatchSIMonly(pricePlanGroupId)
        const result = yield call(apis.requestSaveToCart, templateOrder, recalculateOnly, false, simOnlyValue, false, false);
        const errorGroup = groupTopBottomErrors(result.data);
        const cartData = {
          ...orderDetails,
          ...result.data
        }
        yield put(actions.saveToCart.success({ ...cartData, errorGroup, checkout }));
      }
      yield put(actions.saveSmartTemplate.success(templateData));
      if ([BUY_ADD_ONS, RRP_EQUIPMENT, RRP_ACCESSORY].includes(shoppingCartType)) {
        yield call(navigate, 'ORDER_REVIEW');
      }
      callback()
    }
  } catch (error) {
    yield put(actions.saveSmartTemplate.failure(error));
  }
}

function* editSmartTemplate(reqData) {
  try {
    const templateId = reqData.templateId;
    // yield put(actions.setDeleteSmartTemplateId(templateId))
    const result = yield call(apis.requestTemplateDetails, templateId);
    if (result.data?.cartDetails) {
      let cartDetail = result.data.cartDetails;
      const pathUrl = smartTemplateCategories.filter((cat) => reqData.category === cat.value)[0].pathUrl
      yield put(actions.createNewOrder.request({
        shoppingCartType: reqData.category,
        templateName: result.data.name,
        isSmartTemplate: true,
        deleteTemplateId: templateId,
        cartDetails: cartDetail
      })
      );
      navigate(getPathURL(pathUrl));
    }
  } catch (error) {
    yield put(actions.editSmartTemplate.failure(error));
  }
}

function* refitLineSubmit(reqData) {
  try {
    const result = yield call(apis.refitLineSubmit, reqData.reqData);
    yield put(actions.refitLineSubmit.success(result.data));
    if (result.data?.redirectUrl) {
      // eslint-disable-next-line no-unused-expressions
      window.location.replace(result.data.redirectUrl);
    } else {
      yield call(navigate, 'ORDER_SUCCESS', {
        orderId: result.data?.orderId
      });
    }
    if (result.status !== 200) {
      yield call(navigate, 'ORDER_FAILURE', {});
    }
  } catch (error) {
    yield put(actions.refitLineSubmit.failure(error));
  }
}

function* pdpaQrEsimRetrieval({ reqData }: any) {
  try {
    const result = yield call(apis.ordereSimQrPdpa, reqData);
    yield put(actions.esimQRPdpaRetrieval.success(result.data));
  } catch (error) {
    yield put(actions.esimQRPdpaRetrieval.failure(error));
  }
}

function* getBulkOrderDetailsByItemId({ payload }: IGetBulkOrderDetails) {
  try {
    const result = yield call(apis.getBulkOrderDetailsByItemId, payload);
    const errorGroup = groupAllErrors(result.data);
    if (!isEmpty(errorGroup)) yield put(actions.updateErrorGroup(errorGroup));
    const currentOrder = getBulkOrderCart(result.data.order)
    const isBulk = currentOrder.cartDetails?.pricePlan?.noOfLines > 1;
    const orderData = {
      ...currentOrder,
      cartDetails: { ...currentOrder.cartDetails, isBulk }
    };
    yield putResolve(actions.getOrderDetails.success(orderData));

    const orderReport = getBulkOrderReport(result.data.order);
    yield putResolve(actions.getBulkOrderDetailsByItemId.success(orderReport));
  } catch (error) {
    yield put(actions.getBulkOrderDetailsByItemId.failure(error));
  }
}

export default function* orderSagas() {
  yield takeLatest('ORDER_SAVE_TO_CART_REQUEST', saveToCartSaga);
  yield takeLatest('ORDER_CREATE_NEW_ORDER_REQUEST', createNewOrder);
  yield takeLatest(ORDER_GET_ORDER_DETAILS_REQUEST, fetchOrderDetails);
  yield takeLatest('ORDER_STORE_SELECTION', storeSelection);
  yield takeLatest(SUBMIT_ORDER_REQUEST, submitOrder);
  yield takeLatest(ORDER_PAYMENT_DETAILS_REQUEST, getPaymentDetails);
  yield takeLatest(GET_ELIGIBLE_LINE_COUNT_REQUEST, getEligibleLineCount);
  yield takeLatest(GET_DIST_CARD_PROFILE_REQUEST, getCardProfile);
  yield takeLatest(PDPA_GET_ORDER_DETAILS_REQUEST, fetchPDPAOrderDetails);
  yield takeLatest(TERMINATE_PARENT_SVC_NUM_REQUEST, terminateParentSvcNo);
  yield takeLatest(GET_SIM_CHARGE_REQUEST, getSimCharge);
  yield takeLatest(WAIVER_REQUEST_STATUS_UPDATE_REQUEST, updateWaiverRequestStatus);
  yield takeLatest(GET_WAIVERED_ORDER_REQUEST, getWaiveredOrder);
  yield takeEvery(ORDER_GET_TEMPLATE_ORDER_DETAILS_REQUEST, getTemplateOrderDetails);
  yield takeLatest(ORDER_SAVE_SMART_TEMPLATE_REQUEST, saveSmartTemplate);
  yield takeLatest(EDIT_SMART_TEMPLATE_REQUEST, editSmartTemplate);
  yield takeLatest(REFIT_LINE_SUBMIT_REQUEST, refitLineSubmit);
  yield takeLatest(PDPA_QR_IMAGE_RET_REQUEST, pdpaQrEsimRetrieval);
  yield takeEvery(GET_BULK_ORDER_DETAILS_BY_ITEM_ID_REQUEST, getBulkOrderDetailsByItemId);
}
